'use client';

import React, { useState, useEffect, useRef } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { Link } from '@/lib/i18n/navigation';
import { useAuth } from '@/lib/auth/context';
import { BrandMark } from './BrandMark';
import { LanguageSwitcher } from './LanguageSwitcher';
import {
  CALCULATOR_ITEMS,
  CALCULATOR_NAV_GROUPS,
  getCalculatorsByNavGroup,
  renderCalculatorIcon
} from '@/lib/config/calculators';
import {
  LogOut, LayoutDashboard, Settings, ChevronDown,
  Sun, Menu, X, Bot, Lock,
  Heart, Compass, Clock, Calendar, FileText,
  LayoutGrid, ShieldAlert, Sparkles, ArrowRight
} from 'lucide-react';

interface HoroscopeDropdownItem {
  id: 'today' | 'weekly' | 'monthly' | 'yearly';
  href: string;
}

const HOROSCOPE_ITEMS: HoroscopeDropdownItem[] = [
  {
    id: 'today',
    href: '/horoscope/today-horoscope?period=daily',
  },
  {
    id: 'weekly',
    href: '/horoscope/today-horoscope?period=weekly',
  },
  {
    id: 'monthly',
    href: '/horoscope/today-horoscope?period=monthly',
  },
  {
    id: 'yearly',
    href: '/horoscope/today-horoscope?period=yearly',
  }
];

interface FeatureRowItem {
  id: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
}

const FEATURE_ITEMS: FeatureRowItem[] = [
  { id: 'aiAstrologer', href: '/ai-astrologer', icon: Bot },
  { id: 'aiAstrologerChat', href: '/features/ai-astrologer-chat', icon: Bot },
  { id: 'birthChart', href: '/features/birth-chart', icon: Compass },
  { id: 'dailyPanchang', href: '/features/daily-panchang', icon: Calendar },
  { id: 'kundliMatching', href: '/features/kundli-matching', icon: Heart },
  { id: 'reports', href: '/reports', icon: FileText }
];

interface FreeToolRowItem {
  id: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
}

const FREE_TOOL_ITEMS: FreeToolRowItem[] = [
  { id: 'panchang', href: '/panchang', icon: Calendar },
  { id: 'choghadiya', href: '/choghadiya-calculator', icon: Clock },
  { id: 'rahuKaal', href: '/rahu-kaal-calculator', icon: ShieldAlert },
  { id: 'hora', href: '/hora-calculator', icon: Clock },
  { id: 'babyName', href: '/baby-name-calculator', icon: Sparkles },
  { id: 'horoscope', href: '/horoscope/today-horoscope?period=daily', icon: Sun }
];

function isHoverCapable(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
}

function getCalculatorTitle(
  calc: { id: string; label: string },
  tCalc: (key: any) => string
): string {
  try {
    const raw = tCalc(`${calc.id}.name`);
    if (
      typeof raw === 'string' &&
      raw.trim().length > 0 &&
      !raw.startsWith('calculators.') &&
      !raw.includes(`${calc.id}.name`)
    ) {
      return raw;
    }
  } catch (_) {}
  return calc.label;
}

export function Navbar() {
  const router = useRouter();
  const { user, logout, loading } = useAuth();
  const pathname = usePathname() || '/';

  const tNav = useTranslations('nav');
  const tCommon = useTranslations('common');
  const tCalc = useTranslations('calculators');
  const tHoro = useTranslations('horoscopeNav');
  const tFeatures = useTranslations('navFeatures');
  const tFreeTools = useTranslations('navFreeTools');
  const tGroups = useTranslations('navGroups');

  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false);
  const [mobileExpandedGroup, setMobileExpandedGroup] = useState<string | null>(null);

  const navRef = useRef<HTMLDivElement | null>(null);
  const triggerRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const clearCloseTimer = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const cleanPath = pathname === '/hi' ? '/' : pathname.startsWith('/hi/') ? pathname.slice(3) : pathname;

  const isSectionActive = (sectionId: string): boolean => {
    if (sectionId === 'features') {
      return cleanPath === '/ai-astrologer' || cleanPath === '/features' || cleanPath.startsWith('/features/') || cleanPath === '/reports';
    }
    if (sectionId === 'freeTools') {
      return ['/panchang', '/choghadiya-calculator', '/rahu-kaal-calculator', '/hora-calculator', '/baby-name-calculator'].includes(cleanPath) || cleanPath.startsWith('/horoscope');
    }
    if (sectionId === 'calculators') {
      return cleanPath === '/all-calculators' || CALCULATOR_ITEMS.some(c => c.href === cleanPath);
    }
    if (sectionId === 'horoscopes') {
      return cleanPath.startsWith('/horoscope');
    }
    if (sectionId === 'learn') {
      return cleanPath.startsWith('/blog') || cleanPath.startsWith('/learn');
    }
    if (sectionId === 'pricing') {
      return cleanPath === '/pricing';
    }
    return false;
  };

  useEffect(() => {
    return () => {
      clearCloseTimer();
    };
  }, []);

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape' && activeDropdown) {
        const prev = activeDropdown;
        clearCloseTimer();
        setActiveDropdown(null);
        if (triggerRefs.current[prev]) {
          triggerRefs.current[prev]?.focus();
        }
      }
    }

    function handleClickOutside(event: MouseEvent | TouchEvent) {
      if (navRef.current && !navRef.current.contains(event.target as Node)) {
        clearCloseTimer();
        setActiveDropdown(null);
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [activeDropdown]);

  useEffect(() => {
    clearCloseTimer();
    setActiveDropdown(null);
    setMobileMenuOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const handleMouseEnter = (id: string) => {
    if (!isHoverCapable()) return;
    clearCloseTimer();
    setActiveDropdown(id);
  };

  const handleMouseLeave = (id: string) => {
    if (!isHoverCapable()) return;
    clearCloseTimer();
    closeTimeoutRef.current = setTimeout(() => {
      // Prevent stray mouseleave from closing if focus is inside panel or on the trigger
      if (navRef.current && navRef.current.contains(document.activeElement)) {
        const activeEl = document.activeElement;
        const currentDropdownEl = document.getElementById(`nav-panel-${id}`);
        const triggerEl = triggerRefs.current[id];
        if (
          (currentDropdownEl && currentDropdownEl.contains(activeEl)) ||
          activeEl === triggerEl
        ) {
          return;
        }
      }
      setActiveDropdown((current) => (current === id ? null : current));
    }, 150);
  };

  const handleTriggerClick = (id: string) => {
    if (isHoverCapable()) {
      // On hover-capable devices, click keeps panel open (never toggle closed right after hover opened it)
      clearCloseTimer();
      setActiveDropdown(id);
    } else {
      // On touch devices (no hover), click toggles open and closed
      setActiveDropdown((current) => (current === id ? null : id));
    }
  };

  const handlePanelKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const container = e.currentTarget;
      const focusable = Array.from(
        container.querySelectorAll<HTMLElement>('a[href], button:not([disabled])')
      );
      if (focusable.length === 0) return;
      const currentIndex = focusable.indexOf(document.activeElement as HTMLElement);
      let nextIndex = 0;
      if (e.key === 'ArrowDown') {
        nextIndex = currentIndex < focusable.length - 1 ? currentIndex + 1 : 0;
      } else {
        nextIndex = currentIndex > 0 ? currentIndex - 1 : focusable.length - 1;
      }
      focusable[nextIndex]?.focus();
    }
  };

  const toggleMobileGroup = (group: string) => {
    setMobileExpandedGroup(mobileExpandedGroup === group ? null : group);
  };

  return (
    <header className="border-b border-white/10 bg-[var(--background)]/85 backdrop-blur-md sticky top-0 z-50 transition-colors duration-200">
      <div ref={navRef} className="max-w-[1160px] mx-auto px-4 sm:px-6 h-16 flex items-center justify-between lg:grid lg:grid-cols-[auto_1fr_auto]">
        {/* 1. LOGO / BRAND MARK (Left Column) */}
        <div className="flex items-center justify-start shrink-0">
          <Link
            href="/"
            className="flex items-center gap-2 group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] focus-visible:ring-offset-4 rounded-sm"
            data-testid="brand-logo-link"
          >
            <BrandMark size="md" showWordmark={true} />
          </Link>
        </div>

        {/* 2. CENTER NAV (Desktop: lg+) */}
        <div className="hidden lg:flex items-center justify-center px-2">
          <nav
            className="flex items-center justify-center gap-1 xl:gap-2 text-[13px] font-medium text-[rgba(220,236,255,0.85)] px-4 py-1.5 rounded-full bg-[rgba(0,8,30,0.55)] border border-white/15 backdrop-blur-md shadow-lg"
            data-testid="center-nav"
          >
            {/* Features Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => handleMouseEnter('features')}
              onMouseLeave={() => handleMouseLeave('features')}
            >
              <button
                ref={(el) => { triggerRefs.current['features'] = el; }}
                type="button"
                id="nav-trigger-features"
                aria-haspopup="true"
                aria-expanded={activeDropdown === 'features'}
                onClick={() => handleTriggerClick('features')}
                className={`flex items-center gap-1 px-2.5 py-1 rounded-full transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] ${
                  isSectionActive('features') || activeDropdown === 'features'
                    ? 'text-white font-semibold'
                    : 'text-[rgba(220,236,255,0.85)] hover:text-white'
                }`}
              >
                <span>{tNav('features')}</span>
                <ChevronDown
                  className={`w-3.5 h-3.5 transition-transform duration-200 ${
                    activeDropdown === 'features' ? 'rotate-180 text-white' : ''
                  }`}
                />
              </button>

              {activeDropdown === 'features' && (
                <div
                  id="nav-panel-features"
                  onKeyDown={handlePanelKeyDown}
                  onMouseEnter={() => handleMouseEnter('features')}
                  onMouseLeave={() => handleMouseLeave('features')}
                  className="absolute top-[calc(100%+12px)] left-0 w-[340px] max-w-[calc(100vw-32px)] bg-[rgba(5,10,36,0.96)] border border-[rgba(255,255,255,0.14)] rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.8)] p-2.5 z-50 animate-in fade-in slide-in-from-top-2 duration-150 backdrop-blur-md before:content-[''] before:absolute before:-top-3 before:left-0 before:w-full before:h-3"
              >
                <div className="space-y-1">
                  {FEATURE_ITEMS.map((item) => {
                    const IconComponent = item.icon;
                    const title = tFeatures(`${item.id}.title` as any) || item.id;
                    const subtitle = tFeatures(`${item.id}.subtitle` as any) || '';
                    const isRowActive = cleanPath === item.href;

                    return (
                      <Link
                        key={item.id}
                        href={item.href}
                        onClick={() => setActiveDropdown(null)}
                        className={`flex items-start gap-3 p-2 rounded-xl transition-colors group ${
                          isRowActive ? 'bg-white/15 text-[#5ab4ff]' : 'hover:bg-white/10 text-white'
                        }`}
                      >
                        <div className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center shrink-0 mt-0.5 group-hover:border-[#5ab4ff]/50 transition-colors">
                          <IconComponent className="w-4 h-4 text-[var(--gold)]" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-xs font-semibold text-white group-hover:text-[#5ab4ff] transition-colors truncate">
                            {title}
                          </div>
                          {subtitle && (
                            <div className="text-[11px] text-[rgba(220,236,255,0.82)] group-hover:text-white normal-case whitespace-normal break-words line-clamp-1 mt-0.5 leading-snug">
                              {subtitle}
                            </div>
                          )}
                        </div>
                      </Link>
                    );
                  })}
                </div>

                <div className="pt-2 mt-2 border-t border-[var(--line)]">
                  <Link
                    href="/features"
                    onClick={() => setActiveDropdown(null)}
                    className="flex items-center gap-3 p-2 rounded-xl hover:bg-[var(--card-warm)] text-white group transition-colors"
                  >
                    <div className="w-9 h-9 rounded-lg bg-[var(--gold)]/10 border border-[var(--gold)]/20 flex items-center justify-center shrink-0">
                      <LayoutGrid className="w-4 h-4 text-[var(--gold)]" />
                    </div>
                    <span className="text-xs font-bold group-hover:text-[var(--gold)] transition-colors">
                      {tFeatures('allFeatures.title')}
                    </span>
                  </Link>
                </div>
              </div>
            )}
          </div>

          {/* Free Tools Dropdown */}
          <div
            className="relative"
            onMouseEnter={() => handleMouseEnter('freeTools')}
            onMouseLeave={() => handleMouseLeave('freeTools')}
          >
            <button
              ref={(el) => { triggerRefs.current['freeTools'] = el; }}
              type="button"
              id="nav-trigger-free-tools"
              aria-haspopup="true"
              aria-expanded={activeDropdown === 'freeTools'}
              onClick={() => handleTriggerClick('freeTools')}
              className={`flex items-center gap-1 px-2.5 py-2 rounded-lg transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] ${
                isSectionActive('freeTools') || activeDropdown === 'freeTools'
                  ? 'text-[var(--gold)]'
                  : 'hover:text-white'
              }`}
            >
              <span>{tNav('freeTools')}</span>
              <ChevronDown
                className={`w-3.5 h-3.5 transition-transform duration-200 ${
                  activeDropdown === 'freeTools' ? 'rotate-180 text-[var(--gold)]' : ''
                }`}
              />
            </button>

            {activeDropdown === 'freeTools' && (
              <div
                id="nav-panel-free-tools"
                onKeyDown={handlePanelKeyDown}
                onMouseEnter={() => handleMouseEnter('freeTools')}
                onMouseLeave={() => handleMouseLeave('freeTools')}
                className="absolute top-[calc(100%+8px)] left-0 w-[340px] max-w-[calc(100vw-32px)] bg-[rgba(5,10,36,0.96)] border border-[rgba(255,255,255,0.14)] rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.8)] p-2.5 z-50 animate-in fade-in slide-in-from-top-2 duration-150 backdrop-blur-md before:content-[''] before:absolute before:-top-3 before:left-0 before:w-full before:h-3"
              >
                <div className="space-y-1">
                  {FREE_TOOL_ITEMS.map((item) => {
                    const IconComponent = item.icon;
                    const title = tFreeTools(`${item.id}.title` as any) || item.id;
                    const subtitle = tFreeTools(`${item.id}.subtitle` as any) || '';
                    const isRowActive = cleanPath === item.href.split('?')[0];

                    return (
                      <Link
                        key={item.id}
                        href={item.href}
                        onClick={() => setActiveDropdown(null)}
                        className={`flex items-start gap-3 p-2 rounded-xl transition-colors group ${
                          isRowActive ? 'bg-white/15 text-[#5ab4ff]' : 'hover:bg-white/10 text-white'
                        }`}
                      >
                        <div className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center shrink-0 mt-0.5 group-hover:border-[#5ab4ff]/50 transition-colors">
                          <IconComponent className="w-4 h-4 text-[var(--gold)]" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-xs font-semibold text-white group-hover:text-[#5ab4ff] transition-colors truncate">
                            {title}
                          </div>
                          {subtitle && (
                            <div className="text-[11px] text-[rgba(220,236,255,0.82)] group-hover:text-white normal-case whitespace-normal break-words line-clamp-1 mt-0.5 leading-snug">
                              {subtitle}
                            </div>
                          )}
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Calculators Mega Panel */}
          <div
            className="relative"
            onMouseEnter={() => handleMouseEnter('calculators')}
            onMouseLeave={() => handleMouseLeave('calculators')}
          >
            <button
              ref={(el) => { triggerRefs.current['calculators'] = el; }}
              type="button"
              id="nav-trigger-calculators"
              aria-haspopup="true"
              aria-expanded={activeDropdown === 'calculators'}
              onClick={() => handleTriggerClick('calculators')}
              className={`flex items-center gap-1 px-2.5 py-2 rounded-lg transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] ${
                isSectionActive('calculators') || activeDropdown === 'calculators'
                  ? 'text-[var(--gold)]'
                  : 'hover:text-white'
              }`}
            >
              <span>{tNav('calculators')}</span>
              <ChevronDown
                className={`w-3.5 h-3.5 transition-transform duration-200 ${
                  activeDropdown === 'calculators' ? 'rotate-180 text-[var(--gold)]' : ''
                }`}
              />
            </button>

            {activeDropdown === 'calculators' && (
              <div
                id="nav-panel-calculators"
                onKeyDown={handlePanelKeyDown}
                onMouseEnter={() => handleMouseEnter('calculators')}
                onMouseLeave={() => handleMouseLeave('calculators')}
                className="absolute top-[calc(100%+8px)] left-1/2 -translate-x-1/2 w-[830px] max-w-[calc(100vw-32px)] max-h-[70vh] overflow-y-auto bg-[rgba(5,10,36,0.96)] border border-[rgba(255,255,255,0.14)] rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.8)] p-5 z-50 animate-in fade-in slide-in-from-top-2 duration-150 backdrop-blur-md before:content-[''] before:absolute before:-top-3 before:left-0 before:w-full before:h-3"
              >
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Column 1 */}
                  <div className="space-y-5">
                    {CALCULATOR_NAV_GROUPS.filter(g => g.column === 1).map((group) => {
                      const items = getCalculatorsByNavGroup(group.key);
                      const groupTitle = tGroups(group.titleKey as any) || group.defaultTitle;

                      return (
                        <div key={group.key}>
                          <div className="text-[10px] font-bold uppercase tracking-wider text-[#b9c6ff] pb-1 mb-2 border-b border-white/10">
                            {groupTitle}
                          </div>
                          <div className="space-y-1">
                            {items.map((calc) => {
                              const calcTitle = getCalculatorTitle(calc, tCalc);
                              const isRowActive = cleanPath === calc.href;

                              return (
                                <Link
                                  key={calc.id}
                                  href={calc.href}
                                  onClick={() => setActiveDropdown(null)}
                                  className={`flex items-center gap-2.5 px-2 py-1.5 rounded-lg transition-colors group ${
                                    isRowActive ? 'bg-white/15 text-[#5ab4ff]' : 'hover:bg-white/10 text-white'
                                  }`}
                                >
                                  <div className="w-7 h-7 rounded-md bg-white/5 border border-white/10 flex items-center justify-center shrink-0 group-hover:border-[#5ab4ff]/50 transition-colors">
                                    {renderCalculatorIcon(calc.iconName, 'w-3.5 h-3.5 text-[var(--gold)]')}
                                  </div>
                                  <span className="text-xs font-semibold text-white group-hover:text-[#5ab4ff] transition-colors truncate">
                                    {calcTitle}
                                  </span>
                                </Link>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Column 2 */}
                  <div className="space-y-5">
                    {CALCULATOR_NAV_GROUPS.filter(g => g.column === 2).map((group) => {
                      const items = getCalculatorsByNavGroup(group.key);
                      const groupTitle = tGroups(group.titleKey as any) || group.defaultTitle;

                      return (
                        <div key={group.key}>
                          <div className="text-[10px] font-bold uppercase tracking-wider text-[#b9c6ff] pb-1 mb-2 border-b border-white/10">
                            {groupTitle}
                          </div>
                          <div className="space-y-1">
                            {items.map((calc) => {
                              const calcTitle = getCalculatorTitle(calc, tCalc);
                              const isRowActive = cleanPath === calc.href;

                              return (
                                <Link
                                  key={calc.id}
                                  href={calc.href}
                                  onClick={() => setActiveDropdown(null)}
                                  className={`flex items-center gap-2.5 px-2 py-1.5 rounded-lg transition-colors group ${
                                    isRowActive ? 'bg-white/15 text-[#5ab4ff]' : 'hover:bg-white/10 text-white'
                                  }`}
                                >
                                  <div className="w-7 h-7 rounded-md bg-white/5 border border-white/10 flex items-center justify-center shrink-0 group-hover:border-[#5ab4ff]/50 transition-colors">
                                    {renderCalculatorIcon(calc.iconName, 'w-3.5 h-3.5 text-[var(--gold)]')}
                                  </div>
                                  <span className="text-xs font-semibold text-white group-hover:text-[#5ab4ff] transition-colors truncate">
                                    {calcTitle}
                                  </span>
                                </Link>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Column 3 */}
                  <div className="space-y-5">
                    {CALCULATOR_NAV_GROUPS.filter(g => g.column === 3).map((group) => {
                      const items = getCalculatorsByNavGroup(group.key);
                      const groupTitle = tGroups(group.titleKey as any) || group.defaultTitle;

                      return (
                        <div key={group.key}>
                          <div className="text-[10px] font-bold uppercase tracking-wider text-[#b9c6ff] pb-1 mb-2 border-b border-white/10">
                            {groupTitle}
                          </div>
                          <div className="space-y-1">
                            {items.map((calc) => {
                              const calcTitle = getCalculatorTitle(calc, tCalc);
                              const isRowActive = cleanPath === calc.href;

                              return (
                                <Link
                                  key={calc.id}
                                  href={calc.href}
                                  onClick={() => setActiveDropdown(null)}
                                  className={`flex items-center gap-2.5 px-2 py-1.5 rounded-lg transition-colors group ${
                                    isRowActive ? 'bg-white/15 text-[#5ab4ff]' : 'hover:bg-white/10 text-white'
                                  }`}
                                >
                                  <div className="w-7 h-7 rounded-md bg-white/5 border border-white/10 flex items-center justify-center shrink-0 group-hover:border-[#5ab4ff]/50 transition-colors">
                                    {renderCalculatorIcon(calc.iconName, 'w-3.5 h-3.5 text-[var(--gold)]')}
                                  </div>
                                  <span className="text-xs font-semibold text-white group-hover:text-[#5ab4ff] transition-colors truncate">
                                    {calcTitle}
                                  </span>
                                </Link>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Footer Link Spanning Panel */}
                <div className="mt-5 pt-3 border-t border-[var(--line)] flex items-center justify-between">
                  <Link
                    href="/all-calculators"
                    onClick={() => setActiveDropdown(null)}
                    className="flex items-center justify-between w-full p-2.5 rounded-xl bg-[var(--gold)]/10 text-[var(--gold)] font-bold text-xs hover:bg-[var(--gold)]/20 transition-colors group"
                  >
                    <span>{tNav('viewAllCalculators', { count: CALCULATOR_ITEMS.length })}</span>
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>
            )}
          </div>

          {/* Horoscopes Dropdown */}
          <div
            className="relative"
            onMouseEnter={() => handleMouseEnter('horoscopes')}
            onMouseLeave={() => handleMouseLeave('horoscopes')}
          >
            <button
              ref={(el) => { triggerRefs.current['horoscopes'] = el; }}
              type="button"
              id="nav-trigger-horoscopes"
              aria-haspopup="true"
              aria-expanded={activeDropdown === 'horoscopes'}
              onClick={() => handleTriggerClick('horoscopes')}
              className={`flex items-center gap-1 px-2.5 py-2 rounded-lg transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] ${
                isSectionActive('horoscopes') || activeDropdown === 'horoscopes'
                  ? 'text-[var(--gold)]'
                  : 'hover:text-white'
              }`}
            >
              <span>{tNav('horoscopes')}</span>
              <ChevronDown
                className={`w-3.5 h-3.5 transition-transform duration-200 ${
                  activeDropdown === 'horoscopes' ? 'rotate-180 text-[var(--gold)]' : ''
                }`}
              />
            </button>

            {activeDropdown === 'horoscopes' && (
              <div
                id="nav-panel-horoscopes"
                onKeyDown={handlePanelKeyDown}
                onMouseEnter={() => handleMouseEnter('horoscopes')}
                onMouseLeave={() => handleMouseLeave('horoscopes')}
                className="absolute top-[calc(100%+8px)] left-0 w-[340px] max-w-[calc(100vw-32px)] bg-[rgba(5,10,36,0.96)] border border-[rgba(255,255,255,0.14)] rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.8)] p-2.5 z-50 animate-in fade-in slide-in-from-top-2 duration-150 backdrop-blur-md before:content-[''] before:absolute before:-top-3 before:left-0 before:w-full before:h-3"
              >
                <div className="space-y-1">
                  {HOROSCOPE_ITEMS.map((item) => {
                    let itemLabel: string = item.id;
                    let itemDesc: string = '';
                    try {
                      itemLabel = tHoro(`${item.id}.label` as any) || item.id;
                      itemDesc = tHoro(`${item.id}.description` as any) || '';
                    } catch (_) {}

                    return (
                      <Link
                        key={item.id}
                        href={item.href}
                        onClick={() => setActiveDropdown(null)}
                        className="flex flex-col p-2.5 rounded-xl hover:bg-white/10 transition-colors group min-w-0"
                      >
                        <div className="text-xs font-semibold text-white group-hover:text-[#5ab4ff] transition-colors">
                          {itemLabel}
                        </div>
                        {itemDesc && (
                          <div className="text-[11px] text-[rgba(220,236,255,0.82)] group-hover:text-white normal-case whitespace-normal break-words line-clamp-2 mt-0.5 leading-snug">
                            {itemDesc}
                          </div>
                        )}
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Learn Link */}
          <Link
            href="/blog"
            className={`px-2.5 py-2 rounded-lg transition-colors whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] ${
              isSectionActive('learn') ? 'text-[var(--gold)]' : 'hover:text-white'
            }`}
          >
            {tNav('learn')}
          </Link>

            {/* Pricing Link */}
            <Link
              href="/pricing"
              className={`px-3 py-1 rounded-full transition-colors whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] ${
                isSectionActive('pricing') ? 'text-white font-semibold' : 'text-[rgba(220,236,255,0.85)] hover:text-white'
              }`}
            >
              {tNav('pricing')}
            </Link>
          </nav>
        </div>

        {/* 3. RIGHT SIDE CONTROLS */}
        <div className="flex items-center justify-end gap-2 sm:gap-3 shrink-0">
          {/* Language Switcher */}
          <div className="hidden sm:block">
            <LanguageSwitcher variant="desktop" />
          </div>


          {/* AI Astrologer CTA */}
          <Link
            href="/ai-astrologer"
            className="hidden sm:inline-flex items-center gap-1.5 h-8 px-4 rounded-full text-xs font-semibold text-white bg-gradient-to-r from-white/20 to-white/5 border border-white/35 shadow-[inset_0_1px_0_rgba(255,255,255,0.5)] backdrop-blur-md hover:bg-white/25 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] focus-visible:ring-offset-2 shrink-0"
            data-testid="ask-ai-btn"
          >
            <Bot className="w-3.5 h-3.5" />
            <span>{tNav('aiAstrologer')}</span>
          </Link>

          {/* Auth State: User Settings Dropdown or Sign In */}
          {!loading && user ? (
            <div
              className="relative hidden md:block"
              onMouseEnter={() => handleMouseEnter('userSettings')}
              onMouseLeave={() => handleMouseLeave('userSettings')}
            >
              <button
                ref={(el) => { triggerRefs.current['userSettings'] = el; }}
                onClick={() => handleTriggerClick('userSettings')}
                className="h-[30px] w-[30px] inline-flex items-center justify-center rounded-full border border-[var(--line)] hover:border-[var(--gold)] text-[rgba(220,236,255,0.82)] hover:text-white transition-colors cursor-pointer"
                aria-label="User Settings"
                data-testid="settings-icon-btn"
              >
                <Settings className="w-3.5 h-3.5" />
              </button>

              {activeDropdown === 'userSettings' && (
                <div
                  id="nav-panel-userSettings"
                  onKeyDown={handlePanelKeyDown}
                  onMouseEnter={() => handleMouseEnter('userSettings')}
                  onMouseLeave={() => handleMouseLeave('userSettings')}
                  className="absolute top-[calc(100%+8px)] right-0 w-60 bg-[rgba(5,10,36,0.96)] border border-[rgba(255,255,255,0.14)] rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.8)] backdrop-blur-md p-2 z-50 space-y-1 before:content-[''] before:absolute before:-top-3 before:left-0 before:w-full before:h-3"
                  data-testid="user-settings-dropdown"
                >
                  <div className="px-3 py-2 border-b border-[var(--line)]">
                    <span className="text-[10px] font-bold text-[rgba(220,236,255,0.82)] block">Logged in as</span>
                    <span className="text-xs font-mono text-[#5ab4ff] truncate block">{user.email}</span>
                  </div>

                  <Link
                    href="/dashboard"
                    onClick={() => setActiveDropdown(null)}
                    className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold text-white hover:bg-white/10 transition-colors"
                  >
                    <LayoutDashboard className="w-4 h-4 text-[var(--gold)]" />
                    <span>{tNav('dashboard')}</span>
                  </Link>

                  <Link
                    href="/settings"
                    onClick={() => setActiveDropdown(null)}
                    className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold text-white hover:bg-white/10 transition-colors"
                  >
                    <Settings className="w-4 h-4 text-[var(--gold)]" />
                    <span>{tNav('settings')}</span>
                  </Link>

                  <Link
                    href="/settings#change-password"
                    onClick={() => setActiveDropdown(null)}
                    className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold text-white hover:bg-white/10 transition-colors"
                  >
                    <Lock className="w-4 h-4 text-[var(--gold)]" />
                    <span>{tNav('changePassword')}</span>
                  </Link>

                  <div className="pt-1 border-t border-[var(--line)]">
                    <button
                      onClick={async () => {
                        setActiveDropdown(null);
                        await logout();
                        router.push('/login');
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-medium text-rose-500 hover:bg-rose-500/10 transition-colors text-left cursor-pointer"
                    >
                      <LogOut className="w-4 h-4" />
                      <span>{tNav('logout')}</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <Link
              href="/login"
              className="hidden md:inline-flex items-center gap-1.5 h-8 px-3.5 rounded-full border border-white/20 bg-white/5 text-[rgba(220,236,255,0.85)] hover:text-white hover:border-white/40 text-xs font-medium transition-all cursor-pointer shrink-0"
            >
              {tNav('login')}
            </Link>
          )}

          {/* Mobile Hamburger Toggle Button (below lg / 1024px) */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 rounded-full text-[rgba(220,236,255,0.85)] hover:text-white lg:hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
            aria-label={tCommon('toggleMenu')}
          >
            {mobileMenuOpen ? <X className="w-5 h-5 text-[#5ab4ff]" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* 4. MOBILE DRAWER NAVIGATION MENU (below lg / 1024px) */}
      {mobileMenuOpen && (
        <div
          className="lg:hidden border-t border-[rgba(255,255,255,0.14)] bg-[rgba(5,10,36,0.96)] px-4 pt-3 pb-6 space-y-2 overflow-y-auto max-h-[calc(100vh-4rem)] backdrop-blur-md shadow-[0_20px_50px_rgba(0,0,0,0.8)]"
          data-testid="mobile-nav-drawer"
        >
          {/* Mobile Language Switcher */}
          <div className="pb-2 border-b border-[var(--line)]">
            <LanguageSwitcher variant="mobile" />
          </div>

          {/* Mobile Accordion: Features */}
          <div>
            <button
              onClick={() => toggleMobileGroup('features')}
              className={`w-full min-h-[44px] flex items-center justify-between py-2 text-xs font-semibold cursor-pointer ${
                isSectionActive('features') ? 'text-[var(--gold)]' : 'text-white'
              }`}
            >
              <span>{tNav('features')}</span>
              <ChevronDown
                className={`w-4 h-4 transition-transform duration-200 ${
                  mobileExpandedGroup === 'features' ? 'rotate-180 text-[var(--gold)]' : ''
                }`}
              />
            </button>
            {mobileExpandedGroup === 'features' && (
              <div className="pl-3 py-1 space-y-1 border-l-2 border-[var(--gold)]/30 my-1">
                {FEATURE_ITEMS.map((item) => (
                  <Link
                    key={item.id}
                    href={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`min-h-[44px] flex items-center py-1 text-xs transition-colors ${
                      cleanPath === item.href ? 'text-[var(--gold)] font-bold' : 'text-[rgba(220,236,255,0.82)] hover:text-[var(--gold)]'
                    }`}
                  >
                    {tFeatures(`${item.id}.title` as any) || item.id}
                  </Link>
                ))}
                <Link
                  href="/features"
                  onClick={() => setMobileMenuOpen(false)}
                  className="min-h-[44px] flex items-center py-1 text-xs font-bold text-[var(--gold)] hover:underline"
                >
                  {tFeatures('allFeatures.title')} →
                </Link>
              </div>
            )}
          </div>

          {/* Mobile Accordion: Free Tools */}
          <div>
            <button
              onClick={() => toggleMobileGroup('freeTools')}
              className={`w-full min-h-[44px] flex items-center justify-between py-2 text-xs font-semibold cursor-pointer ${
                isSectionActive('freeTools') ? 'text-[var(--gold)]' : 'text-white'
              }`}
            >
              <span>{tNav('freeTools')}</span>
              <ChevronDown
                className={`w-4 h-4 transition-transform duration-200 ${
                  mobileExpandedGroup === 'freeTools' ? 'rotate-180 text-[var(--gold)]' : ''
                }`}
              />
            </button>
            {mobileExpandedGroup === 'freeTools' && (
              <div className="pl-3 py-1 space-y-1 border-l-2 border-[var(--gold)]/30 my-1">
                {FREE_TOOL_ITEMS.map((item) => (
                  <Link
                    key={item.id}
                    href={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className="min-h-[44px] flex items-center py-1 text-xs text-[rgba(220,236,255,0.82)] hover:text-[var(--gold)] transition-colors"
                  >
                    {tFreeTools(`${item.id}.title` as any) || item.id}
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Mobile Accordion: Calculators */}
          <div>
            <button
              onClick={() => toggleMobileGroup('calculators')}
              className={`w-full min-h-[44px] flex items-center justify-between py-2 text-xs font-semibold cursor-pointer ${
                isSectionActive('calculators') ? 'text-[var(--gold)]' : 'text-white'
              }`}
            >
              <span>{tNav('calculators')}</span>
              <ChevronDown
                className={`w-4 h-4 transition-transform duration-200 ${
                  mobileExpandedGroup === 'calculators' ? 'rotate-180 text-[var(--gold)]' : ''
                }`}
              />
            </button>
            {mobileExpandedGroup === 'calculators' && (
              <div className="pl-3 py-1 space-y-3 border-l-2 border-[var(--gold)]/30 my-1">
                {CALCULATOR_NAV_GROUPS.map((group) => {
                  const items = getCalculatorsByNavGroup(group.key);
                  const groupTitle = tGroups(group.titleKey as any) || group.defaultTitle;

                  return (
                    <div key={group.key} className="space-y-1">
                      <div className="text-[10px] font-bold uppercase tracking-wider text-[rgba(220,236,255,0.82)] pt-1">
                        {groupTitle}
                      </div>
                      {items.map((calc) => {
                        const calcTitle = getCalculatorTitle(calc, tCalc);
                        return (
                          <Link
                            key={calc.id}
                            href={calc.href}
                            onClick={() => setMobileMenuOpen(false)}
                            className="min-h-[44px] flex items-center py-1 text-xs text-[rgba(220,236,255,0.82)] hover:text-[var(--gold)] transition-colors"
                          >
                            {calcTitle}
                          </Link>
                        );
                      })}
                    </div>
                  );
                })}
                <div className="pt-2">
                  <Link
                    href="/all-calculators"
                    onClick={() => setMobileMenuOpen(false)}
                    className="min-h-[44px] flex items-center py-1 text-xs font-bold text-[var(--gold)] hover:underline"
                  >
                    {tNav('viewAllCalculators', { count: CALCULATOR_ITEMS.length })} →
                  </Link>
                </div>
              </div>
            )}
          </div>

          {/* Mobile Accordion: Horoscopes */}
          <div>
            <button
              onClick={() => toggleMobileGroup('horoscopes')}
              className={`w-full min-h-[44px] flex items-center justify-between py-2 text-xs font-semibold cursor-pointer ${
                isSectionActive('horoscopes') ? 'text-[var(--gold)]' : 'text-white'
              }`}
            >
              <span>{tNav('horoscopes')}</span>
              <ChevronDown
                className={`w-4 h-4 transition-transform duration-200 ${
                  mobileExpandedGroup === 'horoscopes' ? 'rotate-180 text-[var(--gold)]' : ''
                }`}
              />
            </button>
            {mobileExpandedGroup === 'horoscopes' && (
              <div className="pl-3 py-1 space-y-1 border-l-2 border-[var(--gold)]/30 my-1">
                {HOROSCOPE_ITEMS.map((item) => {
                  const itemLabel = tHoro(`${item.id}.label` as any) || item.id;
                  return (
                    <Link
                      key={item.id}
                      href={item.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className="min-h-[44px] flex items-center py-1 text-xs text-[rgba(220,236,255,0.82)] hover:text-[var(--gold)] transition-colors"
                    >
                      {itemLabel}
                    </Link>
                  );
                })}
              </div>
            )}
          </div>

          {/* Mobile Plain Links: Learn & Pricing */}
          <Link
            href="/blog"
            onClick={() => setMobileMenuOpen(false)}
            className={`min-h-[44px] flex items-center py-2 text-xs font-semibold transition-colors ${
              isSectionActive('learn') ? 'text-[var(--gold)]' : 'text-white hover:text-[var(--gold)]'
            }`}
          >
            {tNav('learn')}
          </Link>

          <Link
            href="/pricing"
            onClick={() => setMobileMenuOpen(false)}
            className={`min-h-[44px] flex items-center py-2 text-xs font-semibold transition-colors ${
              isSectionActive('pricing') ? 'text-[var(--gold)]' : 'text-white hover:text-[var(--gold)]'
            }`}
          >
            {tNav('pricing')}
          </Link>

          {/* Mobile AI Astrologer CTA */}
          <div className="pt-2">
            <Link
              href="/ai-astrologer"
              onClick={() => setMobileMenuOpen(false)}
              className="min-h-[44px] flex items-center justify-center gap-2 w-full py-2.5 px-4 rounded-xl text-xs font-bold bg-[var(--gold)] text-[var(--ink)] hover:bg-[var(--gold-soft)] transition-colors shadow-xs"
              data-testid="mobile-ai-astrologer-btn"
            >
              <Bot className="w-4 h-4" />
              <span>{tNav('aiAstrologer')}</span>
            </Link>
          </div>

          {/* Auth State (Mobile) */}
          <div className="pt-3 border-t border-[var(--line)] space-y-2">
            {!loading && user ? (
              <div className="space-y-1">
                <div className="px-1 py-1 text-[11px] font-mono text-[rgba(220,236,255,0.82)] truncate">
                  Logged in as <span className="text-[var(--gold)]">{user.email}</span>
                </div>
                <Link
                  href="/dashboard"
                  onClick={() => setMobileMenuOpen(false)}
                  className="min-h-[44px] flex items-center gap-2 py-2 px-1 text-xs font-medium text-white hover:text-[var(--gold)] transition-colors"
                >
                  <LayoutDashboard className="w-4 h-4 text-[var(--gold)]" />
                  <span>{tNav('dashboard')}</span>
                </Link>
                <Link
                  href="/settings"
                  onClick={() => setMobileMenuOpen(false)}
                  className="min-h-[44px] flex items-center gap-2 py-2 px-1 text-xs font-medium text-white hover:text-[var(--gold)] transition-colors"
                >
                  <Settings className="w-4 h-4 text-[var(--gold)]" />
                  <span>{tNav('settings')}</span>
                </Link>
                <button
                  onClick={async () => {
                    setMobileMenuOpen(false);
                    await logout();
                    router.push('/login');
                  }}
                  className="min-h-[44px] flex items-center gap-2 py-2 px-1 text-xs font-medium text-rose-500 hover:text-rose-400 transition-colors w-full text-left cursor-pointer"
                >
                  <LogOut className="w-4 h-4" />
                  <span>{tNav('logout')}</span>
                </button>
              </div>
            ) : (
              <Link
                href="/login"
                onClick={() => setMobileMenuOpen(false)}
                className="min-h-[44px] flex items-center justify-center gap-2 w-full py-2.5 px-4 rounded-xl border border-[var(--line-strong)] bg-[var(--card)] text-white text-xs font-bold hover:border-[var(--gold)] transition-all shadow-xs"
              >
                {tNav('login')}
              </Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
